import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  User,
  Handshake,
  ShieldCheck,
  Truck,
  Search,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Footer from "../components/Footer";
import DemoBanner from "../components/DemoBanner";
import { Button } from "../components/ui/button";

/**
 * Public dual-entrance landing.
 *
 * Route: "/" (public). Any logged-in user is bounced straight to /home
 * so we do not disturb the internal dealer flow.
 *
 * The landing offers two prominent paths side-by-side:
 *   1. Dealers  → /login (existing login pane) or /login?mode=signup (signup pane).
 *   2. Private  → /privati (existing private-customer lead-capture page).
 */
export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Logged-in users go straight to their internal home — zero regression on
  // the existing redirect behavior.
  if (user) return <Navigate to="/home" replace />;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="landing-page">
      {/* Top bar */}
      <header className="w-full">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 md:px-8 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-xl bg-[#0A0A0B] flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none">
                <path d="M4 20 L12 4 L20 20 L17 20 L15.4 16 L8.6 16 L7 20 Z" fill="#ffffff" />
                <path d="M9.7 13 L12 8 L14.3 13 Z" fill="#2563EB" />
              </svg>
            </div>
            <div className="font-heading font-bold text-[15px] tracking-tight text-slate-950">
              AUTEXIO
            </div>
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pb-2">
          <DemoBanner testid="landing-demo-banner" />
        </div>
      </header>

      {/* Payoff */}
      <section className="max-w-6xl w-full mx-auto px-5 md:px-8 pt-6 md:pt-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-[11px] uppercase tracking-[0.24em] text-blue-600 font-semibold">
            {t("landing.overline")}
          </p>
          <h1
            data-testid="landing-tagline"
            className="mt-3 font-heading font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-slate-950 max-w-3xl"
          >
            {t("home.hero_tagline_a")}
            <br />
            <span className="text-blue-600">{t("home.hero_tagline_b")}</span>
          </h1>
          <div
            data-testid="landing-free-badge"
            className="mt-4 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1.5 text-[11px] md:text-xs font-medium text-emerald-800"
          >
            <span>{t("pricing_hint.free_join_full")}</span>
          </div>
        </motion.div>
      </section>

      {/* Two path cards */}
      <section className="flex-1 max-w-6xl w-full mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <DealerCard t={t} />
          <PrivateCard t={t} />
        </div>

        {/* Trust strip */}
        <div className="mt-14 pt-8 border-t border-slate-100">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400 font-semibold text-center">
            {t("landing.trust_overline")}
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <TrustPill Icon={Search} label={t("landing.trust_1")} />
            <TrustPill Icon={Handshake} label={t("landing.trust_2")} />
            <TrustPill Icon={ShieldCheck} label={t("landing.trust_3")} />
            <TrustPill Icon={Truck} label={t("landing.trust_4")} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function DealerCard({ t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.05 }}
      data-testid="landing-card-dealer"
      className="relative overflow-hidden rounded-3xl bg-[#0A0A0B] text-white p-7 md:p-9 flex flex-col shadow-lg"
    >
      <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-blue-600/25 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-[320px] h-[320px] rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="relative z-10 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-blue-300 font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
          {t("landing.dealer_overline")}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
        </div>
        <h2 className="mt-4 font-heading font-bold text-3xl md:text-[34px] tracking-tighter">
          {t("landing.dealer_title")}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-300 max-w-md">
          {t("landing.dealer_body")}
        </p>
        <p className="mt-2 text-[12px] leading-relaxed text-slate-400 max-w-md" data-testid="landing-dealer-note">
          {t("landing.dealer_note_short")}
        </p>
        <ul className="mt-5 space-y-2 text-[13px] text-slate-300">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
            {t("landing.dealer_bullet_1")}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
            {t("landing.dealer_bullet_2")}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
            {t("landing.dealer_bullet_3")}
          </li>
        </ul>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button asChild data-testid="landing-dealer-login-btn" className="h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 shadow-md">
            <Link to="/login">
              {t("landing.dealer_cta_login")}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="landing-dealer-signup-btn"
            className="h-12 rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white font-semibold px-6"
          >
            <Link to="/login?mode=signup">{t("landing.dealer_cta_signup")}</Link>
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function PrivateCard({ t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15 }}
      data-testid="landing-card-private"
      className="relative overflow-hidden rounded-3xl bg-white border border-slate-200 p-7 md:p-9 flex flex-col shadow-sm"
    >
      <div className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full bg-blue-50 blur-3xl" />
      <div className="relative z-10 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-emerald-600 font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {t("landing.private_overline")}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <User className="w-5 h-5 text-emerald-600" />
          </div>
        </div>
        <h2 className="mt-4 font-heading font-bold text-3xl md:text-[34px] tracking-tighter text-slate-950">
          {t("landing.private_title")}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-600 max-w-md">
          {t("landing.private_body")}
        </p>
        <ul className="mt-5 space-y-2 text-[13px] text-slate-600">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
            {t("landing.private_bullet_1")}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
            {t("landing.private_bullet_2")}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
            {t("landing.private_bullet_3")}
          </li>
        </ul>
        <div className="mt-8">
          <Button asChild data-testid="landing-private-cta" className="h-12 rounded-full bg-slate-950 hover:bg-black text-white font-semibold px-6 shadow-md">
            <Link to="/privati">
              {t("landing.private_cta")}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function TrustPill({ Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-slate-600 justify-center sm:justify-start">
      <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      <span className="text-center sm:text-left">{label}</span>
    </div>
  );
}
